import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'

export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent font-medium text-sm outline-none transition-colors duration-(--duration-fast) select-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        outline: 'border-border-strong bg-card text-foreground hover:bg-muted aria-expanded:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-accent',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted',
        riley: 'border-riley-border bg-riley-soft text-riley hover:border-riley',
        destructive: 'bg-destructive-soft text-destructive hover:bg-destructive hover:text-destructive-foreground',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-8 px-3',
        lg: 'h-10 px-4 text-base',
        icon: 'size-8',
        'icon-sm': 'size-7',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
)

export type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * Why the button is disabled (#1228, guard G2). Shown only while `disabled` is true: as visible helper text next
     * to the button (phones have no hover), wired with `aria-describedby`, and as the hover `title`. A disabled
     * Button without one fails `apps/web/src/test/disabled-reason.test.ts`.
     */
    disabledReason?: string
    /**
     * Several buttons share one reason (a row of Count / Adjust / Ignore): render the text once yourself in an
     * element with this id, and each Button points `aria-describedby` at it instead of repeating the text.
     */
    disabledReasonId?: string
    /**
     * Keeps the button focusable while work is in flight and ignores activation until the work settles. A leading icon
     * is swapped for the spinner; a label-only button keeps its width and shows the spinner centred over the hidden
     * label, so nothing reserves space while idle. Keep the ordinary label as children; Button supplies the spinner.
     */
    pending?: boolean
  }

function isLeadingIcon(child: React.ReactNode): child is React.ReactElement {
  if (!React.isValidElement(child) || child.type === React.Fragment) return false
  return typeof child.type !== 'string' || child.type === 'svg'
}

function PendingContent({ children, pending }: { children: React.ReactNode; pending: boolean }) {
  const content = React.Children.toArray(children)
  const icon = isLeadingIcon(content[0]) ? content.shift() : null
  if (!icon) {
    // No icon to swap with: reserve nothing while idle (an empty slot read as a stray left gap). While pending, the
    // label goes transparent (keeping the button's width and its accessible name) and the spinner sits centred over
    // it, so starting work never resizes the button or shifts its neighbours.
    return (
      <>
        <span className={cn('contents', pending && 'text-transparent [&_*]:text-transparent')}>{content}</span>
        {pending ? (
          <Loader2 data-slot="pending-spinner" className="absolute inset-0 m-auto size-4 animate-spin" aria-hidden="true" />
        ) : null}
      </>
    )
  }
  return (
    <>
      <span data-slot="button-icon" className="relative inline-grid size-4 shrink-0 place-items-center">
        <span className={cn('contents', pending && 'invisible')} aria-hidden={pending || undefined}>
          {icon}
        </span>
        {/* Spin only while pending: a rotating box's corners count toward the slot's scroll width even when invisible,
            which made every idle pending-capable Button report 2-3px of overflow (route guard, #1227). */}
        <Loader2 data-slot="pending-spinner" className={cn('absolute size-4', pending ? 'animate-spin' : 'invisible')} aria-hidden="true" />
      </span>
      {content}
    </>
  )
}

/** Every key in Patchbay has a button; pass `title` with the chord so hover shows it. */
export function Button({ className, variant, size, disabledReason, disabledReasonId, pending, children, onClick, ...props }: ButtonProps) {
  const ownId = React.useId()
  const reason = props.disabled && disabledReason ? disabledReason : undefined
  const button = (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), pending !== undefined && 'relative', className)}
      {...props}
      aria-busy={pending ? true : props['aria-busy']}
      onClick={(event) => {
        if (pending) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        onClick?.(event)
      }}
      {...(reason
        ? {
            title: reason,
            'aria-describedby': [props['aria-describedby'], disabledReasonId ?? ownId].filter(Boolean).join(' '),
          }
        : {})}
    >
      {pending === undefined ? children : <PendingContent pending={pending}>{children}</PendingContent>}
    </ButtonPrimitive>
  )
  if (!reason || disabledReasonId) return button
  return (
    <>
      {button}
      <span id={ownId} data-slot="disabled-reason" className="text-xs text-muted-foreground">
        {reason}
      </span>
    </>
  )
}
